import React, { useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import Chevron from 'core-system/Icons/Actions/Chevron'
import {
  DashboardPeriod,
  DashboardProgramCard,
} from '../dashboardTypes'
import ProgramCard, {
  formatCompactMoney,
  getProgramCardAccentColor,
  PROGRAM_TYPE_ICON,
} from './ProgramCard'
import {
  ProgramTypeSection,
  programTypeSections,
} from './groupProgramTypes'

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Section = styled.div`
  background: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  overflow: hidden;
`

const Header = styled.button`
  appearance: none;
  width: 100%;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: 2px solid ${palette.primary.pink500};
    outline-offset: -2px;
  }
`

const ChevronWrap = styled.span<{ $open: boolean }>`
  display: flex;
  color: ${palette.text.secondary};
  transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const IconBadge = styled.div<{ $color: string }>`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.6rem;
  background: ${(props) => `${props.$color}1A`};
  color: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const TitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`

const Metrics = styled.div`
  display: flex;
  align-items: center;
  gap: 1.75rem;
  margin-left: auto;
  flex-shrink: 0;
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 4.5rem;
`

const Panel = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.$open ? '1fr' : '0fr')};
  opacity: ${(props) => (props.$open ? 1 : 0)};
  pointer-events: ${(props) => (props.$open ? 'auto' : 'none')};
  transition:
    grid-template-rows 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.24s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const PanelInner = styled.div`
  min-height: 0;
  overflow: hidden;
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding: 0 1.25rem 1.25rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

interface ProgramTypeSectionsProps {
  cards: DashboardProgramCard[]
  period: DashboardPeriod
  onCardClick?: (card: DashboardProgramCard) => void
}

const formatCount = (value: number) => value.toLocaleString('en-US')

const ProgramTypeSections = ({
  cards,
  period,
  onCardClick,
}: ProgramTypeSectionsProps) => {
  const sections = programTypeSections(cards, period)
  const [openTypes, setOpenTypes] = useState<string[]>([])

  const toggle = (type: string) => {
    setOpenTypes((current) =>
      current.includes(type)
        ? current.filter((item) => item !== type)
        : [...current, type]
    )
  }

  return (
    <Stack data-cy='program-cards-row'>
      {sections.map((section) => (
        <TypeSection
          key={section.type}
          section={section}
          open={openTypes.includes(section.type)}
          period={period}
          onToggle={() => toggle(section.type)}
          onCardClick={onCardClick}
        />
      ))}
    </Stack>
  )
}

const TypeSection = ({
  section,
  open,
  period,
  onToggle,
  onCardClick,
}: {
  section: ProgramTypeSection
  open: boolean
  period: DashboardPeriod
  onToggle: () => void
  onCardClick?: (card: DashboardProgramCard) => void
}) => {
  const accent = getProgramCardAccentColor([section.type])
  const Icon = PROGRAM_TYPE_ICON[section.type]

  return (
    <Section data-cy={`program-type-${section.type}`}>
      <Header
        type='button'
        aria-expanded={open}
        onClick={onToggle}
      >
        <ChevronWrap $open={open}>
          <Chevron width={18} height={18} />
        </ChevronWrap>
        <IconBadge $color={accent}>
          {Icon ? <Icon width={18} height={18} color={accent} /> : null}
        </IconBadge>
        <TitleBlock>
          <Text variant='h4' fontWeight={600}>
            {section.title}
          </Text>
          {section.description ? (
            <Text variant='body1' color={palette.text.secondary}>
              {section.description}
            </Text>
          ) : null}
        </TitleBlock>
        <Metrics>
          <Stat>
            <Text variant='h4' fontWeight={600}>
              {section.programs}
            </Text>
            <Text variant='body1' color={palette.text.secondary}>
              Programs
            </Text>
          </Stat>
          <Stat>
            <Text variant='h4' fontWeight={600}>
              {formatCount(section.enrolled)}
            </Text>
            <Text variant='body1' color={palette.text.secondary}>
              Enrolled
            </Text>
          </Stat>
          <Stat>
            <Text variant='h4' fontWeight={600}>
              {section.sitesActive}/{section.sitesTotal}
            </Text>
            <Text variant='body1' color={palette.text.secondary}>
              Sites active
            </Text>
          </Stat>
          <Stat>
            <Text variant='h4' fontWeight={600}>
              {formatCompactMoney(section.spendCents)}
            </Text>
            <Text variant='body1' color={palette.text.secondary}>
              Spend
            </Text>
          </Stat>
        </Metrics>
      </Header>
      <Panel $open={open} aria-hidden={!open} inert={!open}>
        <PanelInner>
          <Cards>
            {section.cards.map((card) => (
              <ProgramCard
                key={card.launchId}
                card={card}
                period={period}
                onClick={onCardClick}
              />
            ))}
          </Cards>
        </PanelInner>
      </Panel>
    </Section>
  )
}

export default React.memo(ProgramTypeSections)
